import { states } from "constants/states";
import { isValidEmail } from "helpers/email";
import { Label } from "layout/typography/Label";
import { handleChangePhoneWithMask } from "pages/CreateOrder/utils/mask";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { FileUpload, ItemTemplateOptions } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

import {
  rcmTypesDropdown,
  specialtyDropdown,
} from "../../../helpers/providerConstants";
import { formValidationConstants } from "../helpers/formValidations";

type HospitalDetailsFields =
  | "hospitalName"
  | "addressLine1"
  | "addressLine2"
  | "state"
  | "city"
  | "zipcode"
  | "rcm"
  | "specialty"
  | "publicContactName"
  | "publicContactRole"
  | "eligibilityEnabled"
  | "publicContactPhone"
  | "npiNumber"
  | "patientChatBotId"
  | "contractManagementChatBotId"
  | "emailToReceiveNotification"
  | "frontDeskEmployeesToReceiveDailyEmail";

const StyledFileUpload = styled(FileUpload)`
  display: flex !important;
  flex-direction: row-reverse !important;
  justify-content: flex-end !important;
  gap: 1rem;

  .p-fileupload-buttonbar {
    padding: 0;
    background-color: transparent;
    border: none;
  }
  .p-fileupload-content {
    padding: 0;
    border-radius: 0.5rem;
  }
  .p-fileupload-row > div {
    padding: 0 !important;
  }
`;

const StyledButton = styled(Button)`
  padding: 0;
  border: none;
  background-color: white !important;
  position: absolute;
  top: -10px;
  right: -10px;
  padding: 5px !important;
  height: 30px !important;
  width: 30px !important;
`;

export function HospitalDetails() {
  const {
    control,
    trigger,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<any>();
  const [expanded, setExpanded] = useState(true);
  const eligibilityEnabled = watch("eligibilityEnabled");
  const initialAccountLogoUrlField = watch("initialAccountLogoUrl");
  const [initialAccountLogoUrl, setInitialAccountLogoUrl] = useState<
    string | null
  >(initialAccountLogoUrlField);

  useEffect(() => {
    trigger("npiNumber");
  }, [eligibilityEnabled, trigger]);

  async function handleOnBlurField({
    onBlur,
    field,
  }: {
    onBlur: () => void;
    field: HospitalDetailsFields;
  }) {
    trigger(field);
    onBlur();
  }

  function getFormErrorMessage(name: string) {
    return (
      errors[name] && (
        <small className="p-error">{errors[name]?.message as string}</small>
      )
    );
  }

  const itemTemplate = (inFile: object, props: ItemTemplateOptions) => {
    const file = inFile as File;
    const objectUrl = URL.createObjectURL(file);
    return (
      <div className="flex align-items-center gap-3">
        <img
          src={objectUrl}
          alt={file.name}
          style={{
            width: "80px",
            height: "80px",
            objectFit: "contain",
            borderRadius: "8px",
          }}
        />
        <StyledButton
          type="button"
          icon="pi pi-times"
          className="p-button-outlined p-button-sm p-button-rounded p-button-danger ml-auto"
          onClick={(e) => {
            // eslint-disable-next-line react/prop-types
            props.onRemove(e);
            setInitialAccountLogoUrl(null);
            setValue("accountLogoUrl", null);
          }}
        />
      </div>
    );
  };

  const emptyTemplate = () => {
    if (initialAccountLogoUrl) {
      return (
        <div className="flex align-items-center gap-3">
          <img
            src={initialAccountLogoUrl}
            alt="Account logo"
            style={{
              width: "80px",
              height: "80px",
              objectFit: "contain",
              borderRadius: "8px",
            }}
          />
          <StyledButton
            type="button"
            icon="pi pi-times"
            className="p-button-outlined p-button-sm p-button-rounded p-button-danger ml-auto"
            onClick={() => {
              setInitialAccountLogoUrl(null);
              setValue("accountLogoUrl", null);
            }}
          />
        </div>
      );
    }
    return (
      <div className="flex align-items-center flex-column">
        <i
          className="pi pi-upload p-3"
          style={{
            fontSize: "1.5em",
            borderRadius: "50%",
            color: "var(--gray-400)",
          }}
        />
      </div>
    );
  };

  const convertToBase64 = (file: any) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
      };
    });
  };

  const customBase64Uploader = async (event: any) => {
    setInitialAccountLogoUrl(null);
    const file = event.files[0];
    const convertedFile = await convertToBase64(file);
    setValue("accountLogoUrl", convertedFile);
  };

  const uploadOptions = {
    style: { display: "none" },
  };

  return (
    <div className="block bg-white border-round-xl p-4 border-1 border-gray-300">
      <button
        type="button"
        className="flex align-items-center justify-content-between w-full border-none bg-transparent p-0 cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="text-black font-bold text-lg m-0">
          Hospital Details
        </span>
        <i
          className={`pi pi-chevron-down text-gray-600 transition-duration-200 transition-all ${
            expanded ? "rotate-180" : ""
          }`}
          style={{ fontSize: "0.75rem" }}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="formgrid grid mt-4">
          <div className="field col-12 md:col-3">
            <Label htmlFor="accountLogoUrl">Logo</Label>
            <Controller
              name="accountLogoUrl"
              control={control}
              render={({ field: { ref, name } }) => (
                <StyledFileUpload
                  ref={ref}
                  name={name}
                  accept="image/*"
                  maxFileSize={1000000}
                  itemTemplate={itemTemplate}
                  emptyTemplate={emptyTemplate}
                  uploadOptions={uploadOptions}
                  customUpload
                  uploadHandler={customBase64Uploader}
                  auto
                  chooseLabel="Upload"
                />
              )}
            />
          </div>
          <div className="field col-12 md:col-9 flex flex-column">
            <Label htmlFor="hospitalName">
              Hospital Name <span className="text-red-500">*</span>
            </Label>
            <Controller
              name="hospitalName"
              control={control}
              rules={{
                required: formValidationConstants.HOSPITAL_DETAILS.NAME,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <InputText
                    className="w-full"
                    id="hospitalName"
                    autoComplete="off"
                    value={value || ""}
                    placeholder="Enter hospital name here"
                    aria-labelledby="hospitalName"
                    onBlur={() => {
                      handleOnBlurField({ onBlur, field: "hospitalName" });
                    }}
                    onChange={onChange}
                  />

                  {getFormErrorMessage("hospitalName")}
                </>
              )}
            />
          </div>

          <div className="field col-4 hidden">
            <Label htmlFor="specialty">Specialty</Label>
            <Controller
              data-testid="specialty"
              name="specialty"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  id="specialty"
                  data-testid="specialty__Dropdown"
                  value={value}
                  onChange={onChange}
                  options={Object.values(specialtyDropdown)}
                  optionLabel="name"
                  placeholder="Select"
                  className="w-full"
                  disabled
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-3">
            <Label htmlFor="addressLine1">Address Line 1</Label>
            <Controller
              name="addressLine1"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputText
                  className="w-full"
                  id="addressLine1"
                  autoComplete="off"
                  value={value || ""}
                  placeholder="Address line 1"
                  aria-labelledby="addressLine1"
                  onBlur={() => {
                    handleOnBlurField({ onBlur, field: "addressLine1" });
                  }}
                  onChange={onChange}
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-3">
            <Label htmlFor="city">City</Label>
            <Controller
              data-testid="city"
              name="city"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputText
                  className="w-full"
                  id="city"
                  autoComplete="off"
                  value={value || ""}
                  placeholder="Enter city here"
                  aria-labelledby="city"
                  onBlur={() => {
                    handleOnBlurField({ onBlur, field: "city" });
                  }}
                  onChange={onChange}
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-3">
            <Label htmlFor="state">State</Label>
            <Controller
              data-testid="state"
              name="state"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  id="state"
                  name="state"
                  data-testid="states__Dropdown"
                  value={value}
                  onChange={onChange}
                  options={states}
                  optionLabel="name"
                  placeholder="Select"
                  className="w-full"
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-3">
            <Label htmlFor="zipcode">Zip Code</Label>
            <Controller
              name="zipcode"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputText
                  className="w-full"
                  id="zipcode"
                  autoComplete="off"
                  value={value || ""}
                  placeholder="Enter postal code here"
                  aria-labelledby="zipcode"
                  onBlur={() => {
                    handleOnBlurField({ onBlur, field: "zipcode" });
                  }}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <div className="field col-12 md:col-3">
            <Label htmlFor="npiNumber">NPI</Label>
            <Controller
              name="npiNumber"
              control={control}
              rules={{
                required: eligibilityEnabled
                  ? formValidationConstants.HOSPITAL_DETAILS.NPI
                  : false,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <InputText
                    id="npiNumber"
                    autoComplete="off"
                    value={value || ""}
                    placeholder="Enter NPI number here"
                    aria-labelledby="npiNumber"
                    className={`w-full ${errors.npiNumber ? "p-invalid" : ""}`}
                    onBlur={() => {
                      handleOnBlurField({ onBlur, field: "npiNumber" });
                    }}
                    onChange={onChange}
                  />
                  {getFormErrorMessage("npiNumber")}
                </>
              )}
            />
          </div>

          {/* <div className="field md:col-6 col-12">
            <Label htmlFor="rcm">RCM</Label>
            <Controller
              data-testid="rcm"
              name="rcm"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  id="rcm"
                  data-testid="rcm__Dropdown"
                  value={value}
                  onChange={onChange}
                  options={Object.values(rcmTypesDropdown)}
                  optionLabel="name"
                  placeholder="Select"
                  className="w-full"
                />
              )}
            />
          </div> */}

          <div className="field md:col-3 col-12">
            <Label htmlFor="publicContactName">Public Contact Name</Label>
            <Controller
              name="publicContactName"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputText
                  autoComplete="off"
                  className="w-full"
                  value={value || ""}
                  onChange={onChange}
                  id="publicContactName"
                  aria-labelledby="publicContactName"
                  placeholder="Enter contact name code"
                  onBlur={() => {
                    handleOnBlurField({ onBlur, field: "publicContactName" });
                  }}
                />
              )}
            />
          </div>

          <div className="field md:col-3 col-12">
            <Label htmlFor="publicContactRole">Role</Label>
            <Controller
              name="publicContactRole"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputText
                  autoComplete="off"
                  className="w-full"
                  value={value || ""}
                  onChange={onChange}
                  id="publicContactRole"
                  aria-labelledby="publicContactRole"
                  placeholder="Enter contact role code"
                  onBlur={() => {
                    handleOnBlurField({ onBlur, field: "publicContactRole" });
                  }}
                />
              )}
            />
          </div>

          <div className="field md:col-3 col-12">
            <Label htmlFor="publicContactPhone">Phone</Label>
            <Controller
              name="publicContactPhone"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputText
                  className="w-full"
                  id="publicContactPhone"
                  autoComplete="off"
                  value={value || ""}
                  placeholder="Enter phone number here"
                  aria-labelledby="publicContactPhone"
                  onBlur={() => {
                    handleOnBlurField({ onBlur, field: "publicContactPhone" });
                  }}
                  onChange={(e) => {
                    handleChangePhoneWithMask(e, onChange);
                  }}
                />
              )}
            />
          </div>
          <div className="field col-12 md:col-6">
            <Label htmlFor="emailToReceiveNotification">
              Emails to Receive Order Payment Notifications
            </Label>
            <Controller
              name="emailToReceiveNotification"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Chips
                  id="emailToReceiveNotification"
                  name="emailToReceiveNotification"
                  aria-labelledby="emailToReceiveNotification"
                  value={value}
                  onChange={onChange}
                  className="w-full"
                  placeholder="Enter emails"
                  onAdd={(event) => {
                    const isValid = isValidEmail(event.value);
                    return isValid;
                  }}
                  onBlur={() => {
                    handleOnBlurField({
                      onBlur,
                      field: "emailToReceiveNotification",
                    });
                  }}
                />
              )}
            />
          </div>
          <div className="field col-12 md:col-6">
            <Label htmlFor="frontDeskEmployeesToReceiveDailyEmail">
              Front desk employees to receive daily email
            </Label>
            <Controller
              name="frontDeskEmployeesToReceiveDailyEmail"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Chips
                  id="frontDeskEmployeesToReceiveDailyEmail"
                  name="frontDeskEmployeesToReceiveDailyEmail"
                  aria-labelledby="frontDeskEmployeesToReceiveDailyEmail"
                  value={value}
                  onChange={onChange}
                  className="w-full"
                  placeholder="Enter front desk employees to receive daily email"
                  onAdd={(event) => {
                    const isValid = isValidEmail(event.value);
                    return isValid;
                  }}
                  onBlur={() => {
                    handleOnBlurField({
                      onBlur,
                      field: "frontDeskEmployeesToReceiveDailyEmail",
                    });
                  }}
                />
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
